import React, { useEffect } from 'react';
import { Drawer, LoadingSpinner } from 'components/common';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import EmployeeForm from 'components/app/organization/employee/EmployeeForm';
import { getEmployee } from 'redux/organization/employeeSlice';

/**
 * Edits an employee without leaving the chart.
 *
 * A drawer rather than a new tab: the chart carries state that is expensive to
 * rebuild by hand — zoom, which branches are open, who is selected — and a tab
 * switch or a route change throws all of it away for a two-field correction.
 *
 * The org chart payload is deliberately light (id, name, manager, department,
 * site), while EmployeeForm needs the full record, so the drawer fetches it on
 * open and shows a spinner until the right one has arrived. Guarding on
 * `record.id === employeeId` matters: recordData is shared slice state and may
 * still hold whoever was opened last.
 */
const OrgChartEditDrawer = ({ employeeId, employeeName, onClose, onSaved }) => {
  const dispatch = useDispatch();
  const record = useSelector((state) => state.organization_employee.recordData);

  useEffect(() => {
    if (employeeId) dispatch(getEmployee(employeeId));
  }, [dispatch, employeeId]);

  const ready = record && record.id === employeeId;

  return (
    <Drawer
      show={Boolean(employeeId)}
      onHide={onClose}
      title={`Edit ${employeeName || 'employee'}`}
      width="58%"
      minWidth="520px"
      backdropClick={false}
    >
      {ready ? (
        <EmployeeForm
          title="Edit Employee"
          employee={record}
          embedded
          onSaved={() => {
            onSaved?.();
            onClose();
          }}
          onCancel={onClose}
        />
      ) : (
        <div className="tw-text-center tw-py-16"><LoadingSpinner /></div>
      )}
    </Drawer>
  );
};

OrgChartEditDrawer.propTypes = {
  employeeId: PropTypes.number,
  employeeName: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
};

export default OrgChartEditDrawer;
